import React from "react";
import { Avatar, Box, ButtonBase, Chip, Stack, Tooltip, Typography } from "@mui/material";
import {
    AccessTime,
    ChatBubbleOutline,
    CheckBoxOutlined,
    Flag,
    Inventory2Outlined,
    PersonOutline,
    ReceiptLongOutlined,
    Repeat,
} from "@mui/icons-material";
import { Task, TaskPerson, TaskPriority, TaskStatus } from "../../../services/taskService";
import { PERMISSION_TEXT } from "../taskPermissions";
import {
    avatarColor,
    dueState,
    formatDue,
    initials,
    personName,
    PRIORITY_META,
    repeatLabel,
    STATUS_META,
} from "../taskUtils";

// ── People ──────────────────────────────────────────────────────────────────

interface StaffAvatarProps {
    person?: TaskPerson | null;
    userId?: string | null;
    size?: number;
    /** Shown on hover; defaults to the person's name. */
    tooltip?: string | false;
}

export const StaffAvatar: React.FC<StaffAvatarProps> = ({ person, userId, size = 26, tooltip }) => {
    const id = person?.user_id ?? userId ?? null;
    if (!id) {
        const empty = (
            <Avatar
                sx={{
                    width: size,
                    height: size,
                    bgcolor: "transparent",
                    color: "text.disabled",
                    border: "1.5px dashed",
                    borderColor: "divider",
                }}
            >
                <PersonOutline sx={{ fontSize: size * 0.62 }} />
            </Avatar>
        );
        return tooltip === false ? empty : <Tooltip title={tooltip || "Unassigned"}>{empty}</Tooltip>;
    }
    const name = person
        ? `${personName(person)}${person.is_active === false ? " (deactivated)" : ""}`
        : "Former staff member";
    const avatar = (
        <Avatar
            sx={{
                width: size,
                height: size,
                bgcolor: avatarColor(id),
                fontSize: size * 0.4,
                fontWeight: 700,
                letterSpacing: 0.2,
            }}
        >
            {initials(name)}
        </Avatar>
    );
    return tooltip === false ? avatar : <Tooltip title={tooltip || name}>{avatar}</Tooltip>;
};

// ── Status & priority ───────────────────────────────────────────────────────

export const StatusDot: React.FC<{ status: TaskStatus; size?: number }> = ({ status, size = 8 }) => (
    <Box
        component="span"
        sx={{ width: size, height: size, borderRadius: "50%", bgcolor: STATUS_META[status].color, flexShrink: 0, display: "inline-block" }}
    />
);

interface StatusPillProps {
    status: TaskStatus;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    disabled?: boolean;
}

export const StatusPill: React.FC<StatusPillProps> = ({ status, onClick, disabled }) => {
    const meta = STATUS_META[status];
    return (
        <ButtonBase
            onClick={onClick}
            disabled={disabled || !onClick}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.4,
                borderRadius: 99,
                bgcolor: meta.soft,
                color: meta.color,
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: "nowrap",
                "&:hover": onClick ? { filter: "brightness(0.97)" } : undefined,
                "&:focus-visible": { outline: "2px solid", outlineColor: meta.color, outlineOffset: 1 },
            }}
        >
            <StatusDot status={status} />
            {meta.label}
        </ButtonBase>
    );
};

export const PriorityFlag: React.FC<{ priority: TaskPriority; withLabel?: boolean; size?: number }> = ({
    priority,
    withLabel,
    size = 16,
}) => {
    const meta = PRIORITY_META[priority];
    const icon = <Flag sx={{ fontSize: size, color: meta.color }} />;
    if (!withLabel) return <Tooltip title={`${meta.label} priority`}>{icon}</Tooltip>;
    return (
        <Stack direction="row" alignItems="center" spacing={0.5} component="span">
            {icon}
            <Typography component="span" variant="body2" sx={{ color: priority === "NORMAL" ? "text.secondary" : meta.color, fontWeight: 600 }}>
                {meta.label}
            </Typography>
        </Stack>
    );
};

// ── Due date ────────────────────────────────────────────────────────────────

const DUE_COLORS = {
    overdue: { color: "#b91c1c", bg: "#fef2f2" },
    today: { color: "#b45309", bg: "#fffbeb" },
    soon: { color: "text.secondary", bg: "transparent" },
    later: { color: "text.secondary", bg: "transparent" },
    none: { color: "text.disabled", bg: "transparent" },
};

interface DueChipProps {
    task: Pick<Task, "due_date" | "due_time" | "status">;
    today: string;
    /** Show "No date" instead of nothing. */
    showEmpty?: boolean;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

export const DueChip: React.FC<DueChipProps> = ({ task, today, showEmpty, onClick }) => {
    if (!task.due_date && !showEmpty) return null;
    const state = dueState(task, today);
    const done = task.status === "DONE";
    const tone = done ? DUE_COLORS.later : DUE_COLORS[state];
    const label = task.due_date ? formatDue(task, today) : "No date";
    return (
        <Box
            component={onClick ? ButtonBase : "span"}
            onClick={onClick}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
                px: tone.bg === "transparent" ? 0 : 0.75,
                py: 0.15,
                borderRadius: 1,
                bgcolor: tone.bg,
                color: tone.color,
                fontSize: 12.5,
                fontWeight: state === "overdue" || state === "today" ? 600 : 500,
                whiteSpace: "nowrap",
                textDecoration: done && task.due_date ? "line-through" : "none",
                fontVariantNumeric: "tabular-nums",
            }}
        >
            <AccessTime sx={{ fontSize: 14 }} />
            {label}
        </Box>
    );
};

// ── Labels & linked records ─────────────────────────────────────────────────

export const LabelChip: React.FC<{ label: string; onDelete?: () => void; onClick?: () => void }> = ({ label, onDelete, onClick }) => (
    <Chip
        size="small"
        label={`#${label}`}
        onDelete={onDelete}
        onClick={onClick}
        sx={{ height: 22, fontSize: 12, fontWeight: 500, bgcolor: "#eef2f1", color: "#18383E", "& .MuiChip-label": { px: 0.9 } }}
    />
);

export type LinkKind = "order" | "customer" | "product";

interface LinkChipsProps {
    task: Pick<Task, "order_id" | "order_number" | "customer_id" | "customer_name" | "product_id" | "product_name">;
    onOpen?: (kind: LinkKind, id: string) => void;
    onDelete?: (kind: LinkKind) => void;
    dense?: boolean;
}

export const LinkChips: React.FC<LinkChipsProps> = ({ task, onOpen, onDelete, dense }) => {
    const links: { kind: LinkKind; id: string; label: string; icon: React.ReactElement }[] = [];
    if (task.order_id) links.push({ kind: "order", id: task.order_id, label: task.order_number || "Order", icon: <ReceiptLongOutlined /> });
    if (task.customer_id) links.push({ kind: "customer", id: task.customer_id, label: task.customer_name || "Customer", icon: <PersonOutline /> });
    if (task.product_id) links.push({ kind: "product", id: task.product_id, label: task.product_name || "Product", icon: <Inventory2Outlined /> });
    if (!links.length) return null;
    return (
        <>
            {links.map((l) => (
                <Chip
                    key={l.kind}
                    size="small"
                    variant="outlined"
                    icon={l.icon}
                    label={l.label}
                    onClick={
                        onOpen
                            ? (e) => {
                                  e.stopPropagation();
                                  onOpen(l.kind, l.id);
                              }
                            : undefined
                    }
                    onDelete={onDelete ? () => onDelete(l.kind) : undefined}
                    sx={{
                        height: dense ? 22 : 26,
                        maxWidth: 220,
                        fontSize: 12,
                        fontWeight: 500,
                        "& .MuiChip-icon": { fontSize: 15, ml: 0.6 },
                        "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
                    }}
                />
            ))}
        </>
    );
};

// ── The small facts row under a title ───────────────────────────────────────

interface TaskFactsProps {
    task: Task;
    today: string;
    /** Hide the due chip (e.g. inside a section already named by its date). */
    hideDue?: boolean;
    onOpenLink?: (kind: LinkKind, id: string) => void;
}

export const TaskFacts: React.FC<TaskFactsProps> = ({ task, today, hideDue, onOpenLink }) => {
    const done = task.checklist_total > 0 && task.checklist_done === task.checklist_total;
    return (
        <Stack direction="row" alignItems="center" useFlexGap flexWrap="wrap" columnGap={1.25} rowGap={0.5} sx={{ color: "text.secondary" }}>
            {!hideDue && <DueChip task={task} today={today} />}
            {task.priority !== "NORMAL" && <PriorityFlag priority={task.priority} size={15} />}
            {task.repeat_rule && (
                <Tooltip title={repeatLabel(task.repeat_rule, task.repeat_interval, task.due_date)}>
                    <Repeat sx={{ fontSize: 16 }} />
                </Tooltip>
            )}
            {task.checklist_total > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.4} sx={{ color: done ? "success.main" : "inherit" }}>
                    <CheckBoxOutlined sx={{ fontSize: 15 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                        {task.checklist_done}/{task.checklist_total}
                    </Typography>
                </Stack>
            )}
            {task.comment_count > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.4}>
                    <ChatBubbleOutline sx={{ fontSize: 14 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {task.comment_count}
                    </Typography>
                </Stack>
            )}
            {task.labels.map((l) => (
                <Typography key={l} variant="caption" sx={{ color: "#18383E", fontWeight: 600 }}>
                    #{l}
                </Typography>
            ))}
            <LinkChips task={task} onOpen={onOpenLink} dense />
        </Stack>
    );
};

// ── Round "complete" button ─────────────────────────────────────────────────

interface CompleteCircleProps {
    done: boolean;
    onToggle: () => void;
    disabled?: boolean;
    size?: number;
}

export const CompleteCircle: React.FC<CompleteCircleProps> = ({ done, onToggle, disabled, size = 22 }) => (
    <Tooltip title={disabled ? PERMISSION_TEXT.complete : done ? "Mark as not done" : "Mark as done"}>
        <span>
            <ButtonBase
                aria-label={done ? "Mark as not done" : "Mark as done"}
                disabled={disabled}
                onClick={(e) => {
                    e.stopPropagation();
                    onToggle();
                }}
                sx={{
                    width: size,
                    height: size,
                    borderRadius: "50%",
                    border: "2px solid",
                    borderColor: done ? "success.main" : "#cbd5e1",
                    bgcolor: done ? "success.main" : "transparent",
                    color: "#fff",
                    flexShrink: 0,
                    transition: "all 120ms ease",
                    opacity: disabled ? 0.45 : 1,
                    "& svg": { opacity: done ? 1 : 0, transition: "opacity 120ms ease" },
                    "&:hover": disabled ? undefined : { borderColor: "success.main", "& svg": { opacity: 1, color: done ? "#fff" : "success.main" } },
                    "&:focus-visible": { outline: "2px solid", outlineColor: "success.main", outlineOffset: 2 },
                }}
            >
                <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 12 12" fill="none" aria-hidden>
                    <path d="M2.5 6.2 5 8.6l4.6-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </ButtonBase>
        </span>
    </Tooltip>
);
